import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { DashboardOverview } from "@/lib/types";
import { Card, CardTitle, StatTile } from "@/components/Card";
import StatusBadge from "@/components/StatusBadge";
import { formatEur } from "@/lib/format";

export default function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard-overview"],
    queryFn: () => api.get<DashboardOverview>("/api/v1/dashboard"),
  });

  if (isLoading) return <div className="text-sm text-zinc-500">Loading dashboard…</div>;
  if (error || !data)
    return <div className="text-sm text-[var(--danger)]">Failed to load dashboard.</div>;

  const { emails24h } = data;
  const replyRate = emails24h.sent > 0 ? Math.round((emails24h.replied / emails24h.sent) * 100) : 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-[var(--fg-muted)]">Pipeline overview &amp; last 24 h</p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile label="Total prospects" value={data.totalProspects} />
        <StatTile label="Active strategies" value={data.activeStrategies} />
        <StatTile
          label="LLM spend today"
          value={formatEur(data.llmSpentTodayEur)}
          sub={`Cap: ${formatEur(data.llmDailyCapEur)}`}
        />
        <StatTile label="Reply rate (24h)" value={`${replyRate}%`} sub={`${emails24h.replied} / ${emails24h.sent}`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardTitle>Pipeline by status</CardTitle>
          <ul className="mt-2 space-y-1.5">
            {Object.entries(data.prospectsByStatus)
              .filter(([, n]) => n > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([status, n]) => (
                <li key={status} className="flex items-center justify-between text-sm">
                  <StatusBadge status={status as keyof typeof data.prospectsByStatus} />
                  <span className="tabular-nums font-medium">{n}</span>
                </li>
              ))}
            {Object.values(data.prospectsByStatus).every((n) => n === 0) ? (
              <li className="text-sm text-[var(--fg-muted)]">No prospects yet.</li>
            ) : null}
          </ul>
        </Card>

        <Card>
          <CardTitle>Email activity (24 h)</CardTitle>
          <dl className="mt-2 grid grid-cols-2 gap-y-1.5 text-sm">
            <dt className="text-[var(--fg-muted)]">Sent</dt>
            <dd className="text-right tabular-nums">{emails24h.sent}</dd>
            <dt className="text-[var(--fg-muted)]">Delivered</dt>
            <dd className="text-right tabular-nums">{emails24h.delivered}</dd>
            <dt className="text-[var(--fg-muted)]">Opened</dt>
            <dd className="text-right tabular-nums">{emails24h.opened}</dd>
            <dt className="text-[var(--fg-muted)]">Replied</dt>
            <dd className="text-right tabular-nums">{emails24h.replied}</dd>
            <dt className="text-[var(--fg-muted)]">Bounced</dt>
            <dd className="text-right tabular-nums text-[var(--danger)]">{emails24h.bounced}</dd>
            <dt className="text-[var(--fg-muted)]">Failed</dt>
            <dd className="text-right tabular-nums text-[var(--danger)]">{emails24h.failed}</dd>
          </dl>
        </Card>
      </div>
    </div>
  );
}
